import { Component } from '@angular/core';
import { SharedModule } from '../../shared.module';
import { NG_VALUE_ACCESSOR } from '@angular/forms';
import { StorageKeys, User } from '../../models/user.model';
import { MarkAsReadPayload, NOTIFICATION_GROUPS, Notification, NotificationGroup, NotificationUnread } from '../../models/notification.model';
import { Router } from '@angular/router';
import { UserService } from '../../services/user/user.service';
import { AuthService } from '../../services/auth/auth.service';
import { NotificationService } from '../../services/notification/notification.service';
import { UtilService } from '../../services/util.service';

@Component({
  selector: 'app-notification-bell',
  standalone: true,
  imports: [SharedModule],
  templateUrl: './notification-bell.component.html',
  styleUrl: './notification-bell.component.scss',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      multi: true,
      useExisting: NotificationBellComponent,
    }
  ],
})
export class NotificationBellComponent {
  sessionUser: User
  notifications: Notification[] = []
  unread: number = 0
  unreadByGroup: { [key: string]: number } = {}
  page: number = 1
  notificationGroups: NotificationGroup[] = NOTIFICATION_GROUPS
  selectedGroup: NotificationGroup = this.getSavedGroup()

  loadingNotifications: boolean = false
  loadingMore: boolean = false
  havingMore: boolean = false
  needLoadNotifications: boolean = true
  notificationVisible: boolean = false

  constructor(
    private router: Router,
    private userService: UserService,
    private notificationService: NotificationService,
    private utilService: UtilService,
  ) {
    this.sessionUser = this.userService.getCurrentUser()
  }

  // last selected group in this session, fallback to "All"
  getSavedGroup(): NotificationGroup {
    const key = sessionStorage.getItem(StorageKeys.NotificationGroup)
    return this.notificationGroups.find(g => g.key == key) || this.notificationGroups[0]
  }

  async ngOnInit() {
    this.notificationService.connect()
    this.notificationService.notificationUpdated.subscribe(msg => {
      this.loadUnreadCount()
      this.needLoadNotifications = true
    })

    this.loadUnreadCount()
  }

  async loadUnreadCount() {
    try {
      const r = await this.notificationService.getUnreadCount()
      this.unread = r.unreadCount
      this.unreadByGroup = this.countUnreadByGroup(r)
    } catch (e) {
      this.utilService.showError(e)
    }
  }

  // "All" uses the total, other groups sum the counts of their event types
  countUnreadByGroup(r: NotificationUnread): { [key: string]: number } {
    const result: { [key: string]: number } = {}
    this.notificationGroups.forEach(g => {
      result[g.key] = g.eventTypes.length == 0
        ? r.unreadCount
        : r.unreadCountByEventType
          .filter(c => g.eventTypes.includes(c.eventType))
          .reduce((sum, c) => sum + c.unreadCount, 0)
    })
    return result
  }

  async loadNotifications() {
    const group = this.selectedGroup
    this.loadingNotifications = true
    let pageSize = 20
    try {
      const res: any = await this.notificationService.getNotifications(this.page, pageSize, group.eventTypes)
      // group was switched while loading, drop stale response
      if (group !== this.selectedGroup) {
        return
      }
      if (res.data) {
        this.notifications = res.data
      }
      this.havingMore = this.notifications.length < res.totalCount
      this.needLoadNotifications = false
    } catch (e) {
      this.utilService.showError(e)
    }

    this.loadingNotifications = false
  }

  async loadMore() {
    const group = this.selectedGroup
    try {
      let pageSize = 20
      this.page++
      const res: any = await this.notificationService.getNotifications(this.page, pageSize, group.eventTypes)
      if (group !== this.selectedGroup) {
        return
      }
      this.notifications = this.notifications.concat(res.data)
      this.havingMore = this.notifications.length < res.totalCount
    } catch (e) {
      this.utilService.showError(e)
    }
  }

  // only mark the selected group, "All" has no event types so it marks everything
  async markAllAsRead() {
    const payload: MarkAsReadPayload = {
      markAll: true,
    }
    if (this.selectedGroup.eventTypes.length) {
      payload.eventTypes = this.selectedGroup.eventTypes
    }

    try {
      await this.notificationService.markAsRead(payload)

      this.needLoadNotifications = true
      this.loadUnreadCount()
      this.loadNotifications()
    } catch (e) {
      this.utilService.showError(e)
    }
  }

  async markAsRead(notificationId: number) {
    try {
      await this.notificationService.markAsRead({
        notificationIds: [notificationId],
      })

      this.needLoadNotifications = true
      this.loadUnreadCount()
      this.loadNotifications()
    } catch (e) {
      this.utilService.showError(e)
    }
  }

  selectGroup(group: NotificationGroup) {
    if (group === this.selectedGroup) {
      return
    }

    this.selectedGroup = group
    sessionStorage.setItem(StorageKeys.NotificationGroup, group.key)
    this.page = 1
    this.notifications = []
    this.havingMore = false
    this.loadNotifications()
  }

  notificationFeedVisibleChange(visible: boolean) {
    if (visible && this.needLoadNotifications) {
      this.page = 1
      this.loadNotifications()
    }
  }

  selectNotification(notif: Notification) {
    this.notificationVisible = false
    this.markAsRead(notif.id)

    if (notif.designTaskId) {
      this.router.navigate([`cases/detail/${notif.ticketId}`], {queryParams: {selected_design_task: notif.designTaskId}})
    } else {
      this.router.navigate([`cases/detail/${notif.ticketId}`])
    }
  }
}
